/**
 * SSR 初始状态 Store
 * 用于存储和访问服务端传递给客户端的状态数据
 *
 * 【工作流程】
 * 1. 服务端获取数据后存入 ssrContext.initialState
 * 2. 服务端渲染时序列化到 HTML: window.__INITIAL_STATE__
 * 3. 客户端从 window.__INITIAL_STATE__ 恢复数据
 * 4. 组件通过这个 store 访问数据
 *
 * 【使用示例】
 * const ssrState = useSSRInitialStateStore();
 * const configInfo = ssrState.data.current.configInfo;
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * SSR 初始状态管理 Store
 *
 * 【Pinia Setup Store 语法】
 * defineStore('store-name', () => { ... })
 * - 第一个参数: store 唯一 ID
 * - 第二个参数: setup 函数，返回 state 和 actions
 */
const useSSRInitialStateStore = defineStore('ssr-state', () => {
  // ========================================================================
  // State - 响应式状态
  // ========================================================================

  /**
   * data - 服务端传递的数据对象
   * ref({}) 创建响应式引用，初始值为空对象
   *
   * 【数据结构】
   * {
   *   current: {
   *     configInfo: {...},      // 网站配置
   *     columnInfo: {...},      // 栏目信息
   *     configPageList: [...],  // 页面配置列表
   *     // ... 其他数据
   *   }
   * }
   */
  const data = ref({});

  /**
   * backTopFlag - 返回顶部按钮显示状态
   * 用于控制页面右下角"回到顶部"按钮的显示
   */
  const backTopFlag = ref(false);

  // ========================================================================
  // Actions - 操作方法
  // ========================================================================

  /**
   * setData - 设置数据对象
   * 在 entry-client.js 中调用，恢复服务端状态
   *
   * @param {object} val - 要设置的数据对象
   *
   * @example
   * if (window.__INITIAL_STATE__) {
   *   const ssrStore = useSSRInitialStateStore(pinia);
   *   ssrStore.setData(window.__INITIAL_STATE__.data);
   * }
   */
  function setData(val) {
    data.value = val;
  }

  /**
   * setDataBackTop - 设置返回顶部按钮的显示状态
   *
   * @param {boolean} val - true 显示，false 隐藏
   */
  function setDataBackTop(val) {
    backTopFlag.value = val;
  }

  // ========================================================================
  // 导出 state 和 actions
  // ========================================================================
  /**
   * 【Pinia Setup Store 返回值】
   * 返回对象中的内容会被 Pinia 处理：
   * - ref/reactive → state
   * - function → actions
   * - computed → getters
   */
  return {
    // State
    data,
    backTopFlag,
    // Actions
    setData,
    setDataBackTop,
  };
});

export default useSSRInitialStateStore;
